import initState from "@shared/init-state-v0.1.0";
import type { InspectInfo, ReactNodeDef } from "@shared/node-v1.0.0";
import type { BaseReactProps } from "@shared/node-v1.0.0";
import { getPortDef } from "@shared/port-v1.0.0";
import systemLoaderAnimation from "@shared/system-loader-animation-v0.3.0";
import ms from "ms";
import initHyperdx from "./app/initHyperdx";
import initLocalDb from "./app/initLocalDb";
import initNetwork from "./app/initNetwork";
import initAuth from "./kuzzle/auth/initAuth";
import initKuzzle from "./kuzzle/initKuzzle";
import initLocalData from "./initLocalData";
import mobile from "./mobile"
import { initSurrealDb } from "./surrealDb";
import initSurrealAuth from "./surrealDb/auth/initSurrealAuth";
import { type NavigationScheme, validateNavigationScheme } from "./validtaion";

export type CollectionDefinitionScheme = {
  collectionName: string;
  version: number;
  scheme: string;
};

export type Props = BaseReactProps & {
  rxDbMultiInstance?: boolean;
  waitRxDb: boolean;

  useNetwork: boolean;

  useBackend: boolean;
  useKuzzle: boolean;
  kuzzleDefaultDbName: string;
  useCustomKuzzle?: boolean;
  kuzzleUrl?: string;
  kuzzlePort?: number;
  waitKuzzle: boolean;

  useSurrealDb: boolean;
  surrealDbNamespace?: string;
  surrealDbDatabase?: string;
  useCustomSurrealDb?: boolean;
  surrealDbUrl?: string;
  waitSurrealDb: boolean;

  useAuth?: boolean;
  authBackend: "kuzzle" | "surrealdb" | "localdata";
  surrealAuthAccessMethod?: string;
  surrealAuthRefreshInterval?: string;
  surrealAuthUserRolePath?: string;
  sessionTimeout: string;
  username?: string;
  password?: string;
  store: AuthStore;

  navigationScheme?: NavigationScheme[];

  useHyperdx: boolean;

  // vezdexodLocalBase
  useLocalData: boolean;
  collectionsDefinition?: CollectionDefinitionScheme[];

  // vezdexodMobile
  useMobileButtons: boolean
};

export type {
  Kuzzle,
  JSONObject,
  ResponsePayload,
  DocumentNotification,
} from "kuzzle-sdk";
export type AuthStore = {
  signedIn: boolean | undefined;
  isLeader: boolean;
  refreshInterval?: NodeJS.Timeout;
};

export type {
  Surreal,
  r,
  Uuid,
  Jsonify,
  surql,
  PreparedQuery,
} from "surrealdb";

import type { createBlob } from "rxdb";
type Rxdb = { createBlob: typeof createBlob };
export type { Rxdb };
export type { RxDatabase } from "rxdb";

import Comp from "../component/App";
import { initNavigation } from "./navigation";

export default {
  hashTag: "#experimental",
  module: { static: Comp },
  inNode: {
    inputs: [
      getPortDef({
        name: "rxDbMultiInstance",
        displayName: "Multi DB instance",
        group: "Custom",
        customGroup: "Local DB",
        type: "boolean",
        default: true,
        visibleAt: "editor",
      }),
      getPortDef({
        name: "waitRxDb",
        displayName: "Wait local DB",
        group: "Custom",
        customGroup: "Local DB",
        type: "boolean",
        default: true,
        visibleAt: "editor",
      }),
      getPortDef({
        name: "useNetwork",
        displayName: "Detect network",
        group: "Custom",
        customGroup: "Network",
        type: "boolean",
        default: false,
        visibleAt: "editor",
      }),
      getPortDef({
        name: "useBackend",
        displayName: "Use backend",
        group: "Custom",
        customGroup: "Backend",
        type: "boolean",
        visibleAt: "editor",
        default: true,
        validate: (p: Props) =>
          p.useBackend
            ? p.useKuzzle ||
              p.useSurrealDb ||
              p.useLocalData ||
              "At least one backend (Kuzzle or SurrealDB) must be selected when backend is enabled"
            : true,
      }),
      getPortDef({
        name: "useKuzzle",
        displayName: "Use Kuzzle",
        group: "Custom",
        customGroup: "Backend",
        type: "boolean",
        visibleAt: "editor",
        default: false,
        dependsOn: (p: Props) => p.useBackend,
      }),
      getPortDef({
        name: "kuzzleDefaultDbName",
        displayName: "Default DB name",
        group: "Custom",
        customGroup: "Kuzzle",
        type: "string",
        visibleAt: "editor",
        dependsOn: (p: Props) => p.useKuzzle,
        validate: (p: Props) => (p.useKuzzle ? !!p.kuzzleDefaultDbName : true),
      }),
      getPortDef({
        name: "waitKuzzle",
        displayName: "Wait DB connection",
        group: "Custom",
        customGroup: "Kuzzle",
        type: "boolean",
        default: true,
        dependsOn: (p: Props) => !!p.useKuzzle,
      }),
      getPortDef({
        name: "useCustomKuzzle",
        displayName: "Custom",
        group: "Custom",
        customGroup: "Kuzzle",
        type: "boolean",
        default: false,
        visibleAt: "editor",
        dependsOn: (p: Props) => p.useKuzzle,
      }),
      getPortDef({
        name: "kuzzleUrl",
        displayName: "URL",
        group: "Custom",
        customGroup: "Kuzzle",
        type: "string",
        default: "localhost",
        visibleAt: "editor",
        dependsOn: (p: Props) => !!p.useCustomKuzzle,
        validate: (p: Props) => (p.useCustomKuzzle ? !!p.kuzzleUrl : true),
      }),
      getPortDef({
        name: "kuzzlePort",
        displayName: "Port",
        group: "Custom",
        customGroup: "Kuzzle",
        type: "number",
        default: 7512,
        dependsOn: (p: Props) => !!p.useCustomKuzzle,
        validate: (p: Props) => (p.useCustomKuzzle ? !!p.kuzzlePort : true),
      }),
      getPortDef({
        name: "useSurrealDb",
        displayName: "Use SurrealDB",
        group: "Custom",
        customGroup: "Backend",
        type: "boolean",
        visibleAt: "editor",
        default: false,
        dependsOn: (p: Props) => p.useBackend,
      }),
      getPortDef({
        name: "surrealDbNamespace",
        displayName: "Namespace",
        group: "Custom",
        customGroup: "SurrealDB",
        type: "string",
        visibleAt: "editor",
        default: "default",
        dependsOn: (p: Props) => !!p.useSurrealDb,
        validate: (p: Props) =>
          p.useSurrealDb ? !!p.surrealDbNamespace : true,
      }),
      getPortDef({
        name: "surrealDbDatabase",
        displayName: "DB name",
        group: "Custom",
        customGroup: "SurrealDB",
        type: "string",
        visibleAt: "editor",
        default: "data",
        dependsOn: (p: Props) => !!p.useSurrealDb,
        validate: (p: Props) => (p.useSurrealDb ? !!p.surrealDbDatabase : true),
      }),
      getPortDef({
        name: "waitSurrealDb",
        displayName: "Wait DB connection",
        group: "Custom",
        customGroup: "SurrealDB",
        type: "boolean",
        default: true,
        dependsOn: (p: Props) => !!p.useSurrealDb,
      }),
      getPortDef({
        name: "useCustomSurrealDb",
        displayName: "Custom SurrealDB",
        group: "Custom",
        customGroup: "SurrealDB",
        type: "boolean",
        default: false,
        visibleAt: "editor",
        dependsOn: (p: Props) => !!p.useSurrealDb,
      }),
      getPortDef({
        name: "surrealDbUrl",
        displayName: "SurrealDB URL",
        group: "Custom",
        customGroup: "SurrealDB",
        type: "string",
        default: "ws://localhost:8000",
        visibleAt: "editor",
        dependsOn: (p: Props) => !!p.useCustomSurrealDb,
        validate: (p: Props) =>
          p.useCustomSurrealDb ? !!p.surrealDbUrl : true,
      }),
      getPortDef({
        name: "useLocalData",
        displayName: "Use localData",
        group: "Custom",
        customGroup: "Backend",
        type: "boolean",
        visibleAt: "editor",
        default: false,
        dependsOn: (p: Props) => p.useBackend,
      }),
      getPortDef({
        name: "collectionsDefinition",
        displayName: "Сollections definition",
        group: "Custom",
        customGroup: "Local data",
        type: "array",
        dependsOn: (p: Props) => p.useLocalData,
      }),
      getPortDef({
        name: "useMobileButtons",
        displayName: "Use mobile buttons",
        group: "Custom",
        customGroup: "Mobile",
        type: "boolean",
        default: false,
      }),      
      getPortDef({
        name: "useAuth",
        displayName: "Use auth",
        group: "Custom",
        customGroup: "Auth",
        type: "boolean",
        visibleAt: "editor",
        default: false,
        dependsOn: (p: Props) =>
          p.useBackend && (p.useKuzzle || p.useSurrealDb || p.useLocalData),
        validate: (p: Props) => {
          if (!p.useAuth) return true;
          if (!p.useBackend) {
            return "Backend must be enabled for authentication";
          }
          if (!p.useKuzzle && !p.useSurrealDb && !p.useLocalData) {
            return "At least one backend (Kuzzle or SurrealDB) must be enabled for authentication";
          }
          return true;
        },
      }),
      getPortDef({
        name: "authBackend",
        displayName: "Auth backend",
        group: "Custom",
        customGroup: "Auth",
        type: [
          { label: "Kuzzle", value: "kuzzle" },
          { label: "SurrealDB", value: "surrealdb" },
          { label: "LocalData", value: "localdata" },
        ],
        default: "kuzzle",
        visibleAt: "editor",
        dependsOn: (p: Props) => !!p.useAuth,
        validate: (p: Props) => {
          if (!p.useAuth) return true;
          if (p.authBackend === "kuzzle" && !p.useKuzzle) {
            return "Kuzzle backend must be enabled for Kuzzle authentication";
          }
          if (p.authBackend === "surrealdb" && !p.useSurrealDb) {
            return "SurrealDB backend must be enabled for SurrealDB authentication";
          }
          if (p.authBackend === "localdata" && !p.useLocalData) {
            return "LocalData backend must be enabled for LocalData authentication";
          }
          return true;
        },
      }),
      getPortDef({
        name: "surrealAuthAccessMethod",
        displayName: "Access method",
        group: "Custom",
        customGroup: "Auth",
        type: "string",
        default: "default",
        visibleAt: "editor",
        dependsOn: (p: Props) => !!(p.useAuth && p.authBackend === "surrealdb"),
        validate: (p: Props) =>
          p.useAuth && p.authBackend === "surrealdb"
            ? !!p.surrealAuthAccessMethod
            : true,
      }),
      getPortDef({
        name: "surrealAuthRefreshInterval",
        displayName: "Token refresh interval",
        group: "Custom",
        customGroup: "Auth",
        type: "string",
        default: "10m",
        visibleAt: "editor",
        dependsOn: (p: Props) => !!(p.useAuth && p.authBackend === "surrealdb"),
        validate: (p: Props) =>
          p.useAuth && p.authBackend === "surrealdb"
            ? p.surrealAuthRefreshInterval &&
              ms(p.surrealAuthRefreshInterval) >= 10000
              ? true
              : "Token refresh interval must be 10 seconds or greater"
            : true,
      }),
      getPortDef({
        name: "surrealAuthUserRolePath",
        displayName: "User role path",
        group: "Custom",
        customGroup: "Auth",
        type: "string",
        default: "role",
        visibleAt: "editor",
        dependsOn: (p: Props) => !!(p.useAuth && p.authBackend === "surrealdb"),
      }),

      getPortDef({
        name: "sessionTimeout",
        displayName: "Session timeout",
        group: "Custom",
        customGroup: "Auth",
        type: "string",
        default: "5d",
        visibleAt: "editor",
        dependsOn: (p: Props) => !!(p.useAuth && p.authBackend === "kuzzle"),
        validate: (p: Props) =>
          p.useAuth && p.authBackend === "kuzzle"
            ? p.sessionTimeout && ms(p.sessionTimeout) >= 360000
              ? true
              : "Session timeout must be 1 hour or greater"
            : true,
      }),
      getPortDef({
        name: "username",
        displayName: "Username",
        group: "Custom",
        customGroup: "Auth",
        type: "string",
        visibleAt: "connection",
        dependsOn: (p: Props) => !!p.useAuth,
      }),
      getPortDef({
        name: "password",
        displayName: "Password",
        group: "Custom",
        customGroup: "Auth",
        type: "string",
        visibleAt: "connection",
        dependsOn: (p: Props) => !!p.useAuth,
      }),
      getPortDef({
        name: "signIn",
        displayName: "Sign in",
        group: "Custom",
        customGroup: "Auth",
        type: "signal",
      }),
      getPortDef({
        name: "navigationScheme",
        displayName: "Scheme",
        group: "Custom",
        customGroup: "Navigation",
        type: "array",
        validate: (p: Props) => {
          if (!p.navigationScheme) return true;
          return validateNavigationScheme(p);
        },
      }),
      getPortDef({
        name: "useHyperdx",
        displayName: "Use HyperDX",
        group: "Custom",
        customGroup: "Debug",
        type: "boolean",
        default: false,
        visibleAt: "editor",
      }),
      getPortDef({
        name: "refreshData",
        displayName: "Refresh data",
        group: "Custom",
        customGroup: "Local data",
        type: "signal",
      }),
      getPortDef({
        name: "hardRefreshData",
        displayName: "Hard refresh data",
        group: "Custom",
        customGroup: "Local data",
        type: "signal",
      }),      
    ],
    outputs: [
      getPortDef({
        name: "networkConnected",
        displayName: "Connected",
        group: "Custom",
        customGroup: "Network",
        type: "boolean",
      }),
      getPortDef({
        name: "networkType",
        displayName: "Type",
        group: "Custom",
        customGroup: "Network",
        type: "string",
      }),
      getPortDef({
        name: "userRole",
        displayName: "User role",
        group: "Custom",
        customGroup: "Auth",
        type: "string",
      }),
      getPortDef({
        name: "signingIn",
        displayName: "Signing in",
        group: "Custom",
        customGroup: "Auth",
        type: "boolean",
      }),
      getPortDef({
        name: "lastSuccessSync",
        displayName: "Last success sync",
        group: "Custom",
        customGroup: "Local data",
        type: "string",
      }),
      getPortDef({
        name: "replicating",
        displayName: "Replicating",
        group: "Custom",
        customGroup: "Local data",
        type: "boolean",
        default: false,
      }),
      getPortDef({
        name: "dataRefreshed",
        displayName: "Data refreshed",
        group: "Custom",
        customGroup: "Local data",
        type: "signal",
      }),
      getPortDef({
        name: "signedIn",
        displayName: "Signed in",
        group: "Custom",
        customGroup: "Auth",
        type: "signal",
      }),
      getPortDef({
        name: "signedOut",
        displayName: "Signed out",
        group: "Custom",
        customGroup: "Auth",
        type: "signal",
      }),
      getPortDef({
        name: "backButtonClick",
        displayName: "Back button click",
        group: "Custom",
        customGroup: "Mobile",
        type: "signal",
      }),
    ],
  },
  afterNode: {
    getInspectInfo: (p, _, noodlNode) => {
      const inspectInfo: InspectInfo[] = [];
      if (noodlNode._internal.connected)
        inspectInfo.push({
          type: "value",
          value: `Network connected: ${noodlNode._internal.connected}`,
        });
      if (noodlNode._internal.connectionType)
        inspectInfo.push({
          type: "value",
          value: `Network type: ${noodlNode._internal.connectionType}`,
        });

      // Backend connections info
      if (p.useBackend) {
        if (p.useKuzzle && R.libs?.Kuzzle) {
          const kuzzleConnected = R.libs.Kuzzle.protocol?.state === "connected";
          inspectInfo.push({
            type: "value",
            value: `Kuzzle: ${kuzzleConnected ? "connected" : "disconnected"}`,
          });
          if (noodlNode._internal.host && noodlNode._internal.port) {
            inspectInfo.push({
              type: "value",
              value: `Kuzzle host: ${noodlNode._internal.host}:${noodlNode._internal.port}`,
            });
          }
          if (p.kuzzleDefaultDbName) {
            inspectInfo.push({
              type: "value",
              value: `Kuzzle DB: ${p.kuzzleDefaultDbName}`,
            });
          }
        }
        if (p.useSurrealDb && R.surreal?.db) {
          const surrealConnected = R.surreal.db.status === "connected";
          inspectInfo.push({
            type: "value",
            value: `SurrealDB: ${surrealConnected ? "connected" : "disconnected"}`,
          });
          if (noodlNode._internal.surrealUrl) {
            inspectInfo.push({
              type: "value",
              value: `SurrealDB URL: ${noodlNode._internal.surrealUrl}`,
            });
          }
          if (p.surrealDbNamespace && p.surrealDbDatabase) {
            inspectInfo.push({
              type: "value",
              value: `SurrealDB: ${p.surrealDbNamespace}/${p.surrealDbDatabase}`,
            });
          }
        }
      }

      // Auth info
      if (p.useAuth && R.db?.states?.auth) {
        inspectInfo.push({
          type: "value",
          value: `Auth backend: ${p.authBackend}`,
        });
        inspectInfo.push({
          type: "value",
          value: `Signed in: ${R.db.states.auth.signedIn}`,
        });
        inspectInfo.push({ type: "text", value: "=== User ===" });
        inspectInfo.push({ type: "value", value: R.user ? R.user : "none" });
      }

      return inspectInfo;
    },
  },
  beforeComponent: {
    initialize: async (p: Props, noodlNode) => {
      console.log("Привет, это app 2.2.0!");

      // Дождемся библиотек и утилит.
      await initState("shared");
      systemLoaderAnimation.progress(15);

      // Навигация
      initNavigation(p);
      // Анимация не нужна, т.к. не больше одной мс
      R.states.init.value = "navigation";

      const {
        project,
        projectVersion,
        projectDefaults,
        environment = "d3",
      } = Noodl.getProjectSettings();
      const { set } = R.libs.just;

      set(R, ["env", "environment"], environment);
      set(R, ["env", "project"], project);
      set(R, ["env", "projectVersion"], projectVersion);

      // Определяем типы бекенда и авторизации
      if (p.useBackend) {
        const backendTypes = [];
        if (p.useKuzzle) backendTypes.push("kuzzle");
        if (p.useSurrealDb) backendTypes.push("surrealdb");
        if (p.useLocalData) backendTypes.push("localdata");
        set(
          R,
          ["env", "backendType"],
          backendTypes.length === 1 ? backendTypes[0] : backendTypes,
        );
      }
      if (p.useAuth) {
        set(R, ["env", "authType"], p.authBackend);
      }
			try {
				if (projectDefaults) set(R, ['params', 'defaults'], Function(`return ${projectDefaults}`)())
			} catch (error) {
				log.error('Project defaults error:', error)
			}

      // Создадим локульную БД для хранения состояния приложения и для возможного offline сценария.
      if (p.waitRxDb) await initLocalDb(p.rxDbMultiInstance);
      else initLocalDb(p.rxDbMultiInstance);
      systemLoaderAnimation.progress(30);
      // Изменим реактивное состояние инициализации приложения.
      R.states.init.value = "localDb";

      // Инициализируем сеть.
      if (p.useNetwork) await initNetwork(noodlNode);
      set(R, ["params", "useNetwork"], p.useNetwork);
      systemLoaderAnimation.progress(45);
      R.states.init.value = "network";

      // Инициализируем бекенд, если нужно.
      // Внутри динамичный импорт, чтобы не загружать, когда не включено.
      if (p.useBackend && (p.useKuzzle || p.useSurrealDb || p.useLocalData)) {
        if (p.useKuzzle && p.useSurrealDb) {
          if (p.waitKuzzle && p.waitSurrealDb)
            await Promise.all([
              initKuzzle(p, noodlNode),
              initSurrealDb(p, noodlNode),
            ]);
          else {
            if (p.waitKuzzle) await initKuzzle(p, noodlNode);
            else initKuzzle(p, noodlNode);
            if (p.waitSurrealDb) await initSurrealDb(p, noodlNode);
            else initSurrealDb(p, noodlNode);
          }
        } else {
          if (p.useKuzzle) {
            if (p.waitKuzzle) await initKuzzle(p, noodlNode);
            else initKuzzle(p, noodlNode);
          }
          if (p.useSurrealDb) {
            if (p.waitSurrealDb) await initSurrealDb(p, noodlNode);
            else initSurrealDb(p, noodlNode);
          }
          if (p.useLocalData) {
            await initLocalData(p, noodlNode);
          }
          if (p.useMobileButtons) {
            await mobile(p, noodlNode);
          }
        }

        R.states.init.value = "backend";
      }

      // 60% внутри initKuzzle
      systemLoaderAnimation.progress(75);

      if (p.useAuth) {
        if (p.authBackend === "kuzzle") {
          await initAuth(p, noodlNode);
        } else if (p.authBackend === "surrealdb") {
          await initSurrealAuth(p, noodlNode);
        }

        // Нужно дождаться не выполнения, а состояния.
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            clearInterval(interval);
            reject(
              new Error(
                "Auth initialization timeout: auth.inited state was not set within 30 seconds",
              ),
            );
          }, 30000); // 30 секунд timeout

          const interval = setInterval(async () => {
            if (R.db.states.auth?.inited) {
              clearTimeout(timeout);
              clearInterval(interval);
              resolve(undefined);
            }
          }, 50);
        });
      }

      systemLoaderAnimation.progress(90);
      R.states.init.value = "auth";

      // Инициализируем отправку логов. Сделаем это послде проверки авторизации, чтобы добавить информацию о пользователе сразу, если есть.
      // Внтури динамичный импорт, чтобы не загружать, когда не включено.
      if (p.useHyperdx) await initHyperdx();
      systemLoaderAnimation.progress(100);

      log.info("R", R);
    },
  },
  disableCustomProps: true,
} satisfies ReactNodeDef;
